/**
 * M07 批量回归 fixture · S2 Day 8b
 *
 * 承接：apps/api/src/csa_api/admin/eval_admin.py（Day 8a · PR #27）
 *
 * 设计意图：
 * - GET /samples 返 36 条金样本子集（mock 6 条覆盖 4 intent）
 * - POST /regress 模拟真后端 baseline 25/36 命中 + 11 fail 模式（同 Day 8.5 baseline）
 *   · 三档 mode 都给值，UI 切换时立即看到差异
 */

import type {
	AssetChangeAuditItem,
	AssetChangeAuditListResponse,
	BadcaseAutoCloseRequest,
	BadcaseEventListResponse,
	BadcaseExecuteRerunRequest,
	BadcaseExecuteRerunResponse,
	BadcaseGraduationRequest,
	BadcaseItem,
	BadcaseListResponse,
	BadcaseRegressionWritebackRequest,
	BadcaseRegressionWritebackResponse,
	BadcaseRerunRequest,
	BadcaseUpdateRequest,
	ConfusionMatrixCell,
	CxScoreRunResponse,
	EvalRegressionBatchCloseBadcasesRequest,
	EvalRegressionBatchCloseBadcasesResponse,
	EvalRegressionBatchCreateRequest,
	EvalRegressionBatchExecuteRequest,
	EvalRegressionBatchExecuteResponse,
	EvalRegressionBatchItem,
	EvalRegressionBatchListResponse,
	EvalRunComparePersistRequest,
	EvalRunCompareResponse,
	EvalRunCompareSnapshotItem,
	EvalRunCompareSnapshotListResponse,
	EvalRunCreateRequest,
	EvalRunItem,
	EvalRunListResponse,
	EvalRunResponse,
	EvalSampleResultItem,
	EvalSampleResultListResponse,
	PerSampleResult,
	RegressionDatasetSampleItem,
	RegressionDatasetSampleListResponse,
	RegressionMode,
	RegressionResponse,
	RegressionSample,
	SamplesListResponse,
} from "@/lib/api/eval";

import { registerFixture } from "./registry";
import type { MockResponse } from "./types";

const MOCK_SAMPLES: RegressionSample[] = [
	{
		id: "S001",
		message: "我想查一下订单状态",
		expected_intent: "order_status_inquiry",
		expected_pb: "PB01_order_query",
	},
	{
		id: "S002",
		message: "What is my order status",
		expected_intent: "order_status_inquiry",
		expected_pb: "PB01_order_query",
	},
	{
		id: "S003",
		message: "订单到哪了",
		expected_intent: "order_status_inquiry",
		expected_pb: "PB01_order_query",
	},
	{
		id: "S009",
		message: "把钱退给我",
		expected_intent: "refund_request",
		expected_pb: "PB02_refund",
	},
	{
		id: "S019",
		message: "这件衣服有 M 码吗",
		expected_intent: "product_inquiry",
		expected_pb: "PB06_pre_sale",
	},
	{ id: "S027", message: "退货政策是怎样的", expected_intent: "knowledge_qa", expected_pb: null },
];

const MOCK_PER_SAMPLE: PerSampleResult[] = [
	{
		sample_id: "S001",
		message: "我想查一下订单状态",
		expected_intent: "order_status_inquiry",
		actual_intent: "order_status_inquiry",
		intent_match: true,
		intent_confidence: 1.0,
		expected_pb: "PB01_order_query",
		actual_pb: "PB01_order_query",
		pb_match: true,
		badcase_candidate: false,
		root_cause_hint: null,
		responsible_asset: null,
		badcase_reason: null,
	},
	{
		sample_id: "S002",
		message: "What is my order status",
		expected_intent: "order_status_inquiry",
		actual_intent: "order_status_inquiry",
		intent_match: true,
		intent_confidence: 1.0,
		expected_pb: "PB01_order_query",
		actual_pb: "PB01_order_query",
		pb_match: true,
		badcase_candidate: false,
		root_cause_hint: null,
		responsible_asset: null,
		badcase_reason: null,
	},
	{
		sample_id: "S003",
		message: "订单到哪了",
		expected_intent: "order_status_inquiry",
		actual_intent: "unknown",
		intent_match: false,
		intent_confidence: 0.0,
		expected_pb: "PB01_order_query",
		actual_pb: null,
		pb_match: false,
		badcase_candidate: true,
		root_cause_hint: "意图识别偏差",
		responsible_asset: "M02 意图规则",
		badcase_reason: "期望 order_status_inquiry，实际 unknown；方案期望 PB01_order_query，实际 无",
	},
	{
		sample_id: "S009",
		message: "把钱退给我",
		expected_intent: "refund_request",
		actual_intent: "unknown",
		intent_match: false,
		intent_confidence: 0.0,
		expected_pb: "PB02_refund",
		actual_pb: null,
		pb_match: false,
		badcase_candidate: true,
		root_cause_hint: "意图识别偏差",
		responsible_asset: "M02 意图规则",
		badcase_reason: "期望 refund_request，实际 unknown；方案期望 PB02_refund，实际 无",
	},
	{
		sample_id: "S019",
		message: "这件衣服有 M 码吗",
		expected_intent: "product_inquiry",
		actual_intent: "unknown",
		intent_match: false,
		intent_confidence: 0.0,
		expected_pb: "PB06_pre_sale",
		actual_pb: null,
		pb_match: false,
		badcase_candidate: true,
		root_cause_hint: "意图识别偏差",
		responsible_asset: "M02 意图规则",
		badcase_reason: "期望 product_inquiry，实际 unknown；方案期望 PB06_pre_sale，实际 无",
	},
	{
		sample_id: "S027",
		message: "退货政策是怎样的",
		expected_intent: "knowledge_qa",
		actual_intent: "return_request",
		intent_match: false,
		intent_confidence: 1.0,
		expected_pb: null,
		actual_pb: "PB03_return",
		pb_match: null,
		badcase_candidate: true,
		root_cause_hint: "意图识别偏差",
		responsible_asset: "M02 意图规则",
		badcase_reason: "期望 knowledge_qa，实际 return_request",
	},
];

const MOCK_MERCHANT_ID = "mer_nY0bZmLI";
const MOCK_EVAL_RUN_ID = "er_p7e3b_mock";
const MOCK_CURRENT_EVAL_RUN_ID = "er_p7e3c_current_mock";
const MOCK_DATASET_ID = "golden15_representative";
const MOCK_DATASET_VERSION = "2026-04-28T00:00:00Z";
const MOCK_CREATED_AT = "2026-05-07T12:20:00Z";

function buildConfusionMatrix(per: PerSampleResult[]): ConfusionMatrixCell[] {
	const counter = new Map<string, number>();
	for (const r of per) {
		const key = `${r.expected_intent}|${r.actual_intent}`;
		counter.set(key, (counter.get(key) ?? 0) + 1);
	}
	const cells: ConfusionMatrixCell[] = [];
	for (const [key, count] of counter.entries()) {
		const [expected = "", actual = ""] = key.split("|");
		cells.push({ expected, actual, count });
	}
	cells.sort((a, b) =>
		a.expected === b.expected
			? a.actual.localeCompare(b.actual)
			: a.expected.localeCompare(b.expected),
	);
	return cells;
}

const intentPassed = MOCK_PER_SAMPLE.filter((r) => r.intent_match).length;
const pbEvaluable = MOCK_PER_SAMPLE.filter((r) => r.pb_match !== null).length;
const pbPassed = MOCK_PER_SAMPLE.filter((r) => r.pb_match === true).length;
const badcaseCandidateCount = MOCK_PER_SAMPLE.filter((r) => r.badcase_candidate).length;

function buildResponse(mode: RegressionMode): RegressionResponse {
	return {
		mode,
		total: MOCK_PER_SAMPLE.length,
		intent_passed: intentPassed,
		intent_hit_rate: intentPassed / MOCK_PER_SAMPLE.length,
		pb_total_evaluable: mode === "intent" ? 0 : pbEvaluable,
		pb_passed: mode === "intent" ? 0 : pbPassed,
		pb_hit_rate: mode === "intent" || pbEvaluable === 0 ? 0 : pbPassed / pbEvaluable,
		confusion_matrix: buildConfusionMatrix(MOCK_PER_SAMPLE),
		per_sample: MOCK_PER_SAMPLE,
	};
}

function toEvalSampleResult(row: PerSampleResult, index: number): EvalSampleResultItem {
	const responsibleAsset =
		row.responsible_asset === "M02 意图规则"
			? { type: "intent_rule" as const, id: "m02_intent_rules" }
			: { type: "unknown" as const, id: null };
	return {
		sample_result_id: `esr_p7e3b_${row.sample_id.toLowerCase()}`,
		eval_run_id: MOCK_EVAL_RUN_ID,
		merchant_id: MOCK_MERCHANT_ID,
		dataset_id: MOCK_DATASET_ID,
		dataset_version: MOCK_DATASET_VERSION,
		sample_id: row.sample_id,
		message: row.message,
		expected_intent: row.expected_intent,
		actual_intent: row.actual_intent,
		intent_match: row.intent_match,
		intent_confidence: row.intent_confidence,
		expected_pb: row.expected_pb,
		actual_pb: row.actual_pb,
		pb_match: row.pb_match,
		badcase_candidate: row.badcase_candidate,
		root_cause_category: row.badcase_candidate ? "intent_error" : null,
		root_cause_hint: row.root_cause_hint,
		responsible_asset_type: row.badcase_candidate ? responsibleAsset.type : null,
		responsible_asset_id: row.badcase_candidate ? responsibleAsset.id : null,
		badcase_reason: row.badcase_reason,
		trace_id: null,
		admin_run_id: null,
		badcase_id: row.badcase_candidate ? `bc_p7e3b_${row.sample_id.toLowerCase()}` : null,
		rerun_status: "not_rerun",
		created_at: new Date(Date.parse(MOCK_CREATED_AT) + index * 1000).toISOString(),
	};
}

const MOCK_SAMPLE_RESULTS: EvalSampleResultItem[] = MOCK_PER_SAMPLE.map(toEvalSampleResult);

function buildPassingBatchSampleResult(
	row: RegressionDatasetSampleItem,
	evalRunId: string,
	index: number,
): EvalSampleResultItem {
	return {
		sample_result_id: `esr_${evalRunId.replace(/^er_/, "")}_${row.sample_id.toLowerCase()}`,
		eval_run_id: evalRunId,
		merchant_id: row.merchant_id,
		dataset_id: row.target_dataset_id,
		dataset_version: row.target_dataset_version ?? "inline",
		sample_id: row.sample_id,
		message: row.message,
		expected_intent: row.expected_intent,
		actual_intent: row.expected_intent,
		intent_match: true,
		intent_confidence: 0.96,
		expected_pb: row.expected_pb,
		actual_pb: row.expected_pb,
		pb_match: row.expected_pb ? true : null,
		badcase_candidate: false,
		root_cause_category: null,
		root_cause_hint: null,
		responsible_asset_type: row.responsible_asset_type,
		responsible_asset_id: row.responsible_asset_id,
		badcase_reason: null,
		trace_id: row.source_trace_id,
		admin_run_id: row.source_admin_run_id,
		badcase_id: row.source_badcase_id,
		rerun_status: "rerun_passed",
		created_at: new Date(Date.parse("2026-05-08T13:02:00Z") + index * 1000).toISOString(),
	};
}

const MOCK_EVAL_RUNS: EvalRunItem[] = [
	{
		eval_run_id: MOCK_CURRENT_EVAL_RUN_ID,
		merchant_id: MOCK_MERCHANT_ID,
		run_name: "P7E-3C 修复后复跑",
		run_type: "regression",
		status: "completed",
		mode: "both",
		dataset_id: MOCK_DATASET_ID,
		dataset_version: MOCK_DATASET_VERSION,
		data_mode: "mock",
		comparison_role: "current",
		baseline_run_id: MOCK_EVAL_RUN_ID,
		asset_versions: {
			intent_rules: "M02 mock rules @ 2026-05-07 fix",
			playbook_mapping: "M03 mock mapping @ 2026-05-07",
			asset_change_audit_refs: [
				{
					audit_id: "aca_intent001",
					asset_type: "intent_rule",
					asset_id: "M02",
					asset_version: "runtime_bundle",
					change_summary: "补充退款弱表达和 unknown 降权规则。",
					created_at: "2026-05-10T09:30:00Z",
				},
				{
					audit_id: "aca_playbook001",
					asset_type: "playbook_mapping",
					asset_id: "M03",
					asset_version: "runtime_config",
					change_summary: "修正退款意图到 PB02 的映射边界。",
					created_at: "2026-05-10T09:35:00Z",
				},
			],
		},
		summary: {
			total: MOCK_PER_SAMPLE.length,
			intent_passed: 4,
			intent_hit_rate: 4 / MOCK_PER_SAMPLE.length,
			pb_total_evaluable: pbEvaluable,
			pb_passed: pbPassed,
			pb_hit_rate: pbPassed / pbEvaluable,
			badcase_candidate_count: badcaseCandidateCount,
		},
		created_by: "codex-p7e3c",
		notes: "P7E-3C baseline/current 对比样例，展示修复收益和新增回退。",
		started_at: "2026-05-07T12:35:00Z",
		completed_at: "2026-05-07T12:35:48Z",
		created_at: "2026-05-07T12:35:00Z",
		updated_at: "2026-05-07T12:35:48Z",
	},
	{
		eval_run_id: MOCK_EVAL_RUN_ID,
		merchant_id: MOCK_MERCHANT_ID,
		run_name: "P7E-3B 回归基线",
		run_type: "regression",
		status: "completed",
		mode: "both",
		dataset_id: MOCK_DATASET_ID,
		dataset_version: MOCK_DATASET_VERSION,
		data_mode: "mock",
		comparison_role: "baseline",
		baseline_run_id: null,
		asset_versions: {
			intent_rules: "M02 mock rules @ 2026-05-07",
			playbook_mapping: "M03 mock mapping @ 2026-05-07",
		},
		summary: {
			total: MOCK_PER_SAMPLE.length,
			intent_passed: intentPassed,
			intent_hit_rate: intentPassed / MOCK_PER_SAMPLE.length,
			pb_total_evaluable: pbEvaluable,
			pb_passed: pbPassed,
			pb_hit_rate: pbPassed / pbEvaluable,
			badcase_candidate_count: badcaseCandidateCount,
		},
		created_by: "codex-p7e3b",
		notes: "后端 MVP 留痕，用于前端 P7E-3C 展示运行历史和 badcase 候选台账。",
		started_at: MOCK_CREATED_AT,
		completed_at: "2026-05-07T12:20:02Z",
		created_at: MOCK_CREATED_AT,
		updated_at: "2026-05-07T12:20:02Z",
	},
];

const MOCK_ASSET_CHANGE_AUDITS: AssetChangeAuditItem[] = [
	{
		audit_id: "aca_intent001",
		merchant_id: MOCK_MERCHANT_ID,
		asset_type: "intent_rule",
		asset_id: "M02",
		asset_version: "runtime_bundle",
		change_type: "updated",
		change_summary: "补充退款弱表达和 unknown 降权规则。",
		source: "admin_console",
		actor: "pm@example.com",
		related_eval_run_id: null,
		related_badcase_id: null,
		metadata: { source_doc: "S6-B" },
		created_at: "2026-05-10T09:30:00Z",
	},
	{
		audit_id: "aca_playbook001",
		merchant_id: MOCK_MERCHANT_ID,
		asset_type: "playbook_mapping",
		asset_id: "M03",
		asset_version: "runtime_config",
		change_type: "updated",
		change_summary: "修正退款意图到 PB02 的映射边界。",
		source: "admin_console",
		actor: "pm@example.com",
		related_eval_run_id: null,
		related_badcase_id: null,
		metadata: { source_doc: "S6-B" },
		created_at: "2026-05-10T09:35:00Z",
	},
];

const MOCK_COMPARE_RESPONSE: EvalRunCompareResponse = {
	baseline: MOCK_EVAL_RUNS[1] as EvalRunItem,
	current: MOCK_EVAL_RUNS[0] as EvalRunItem,
	dataset_match: true,
	metric_deltas: {
		intent_hit_rate: {
			baseline: (MOCK_EVAL_RUNS[1] as EvalRunItem).summary.intent_hit_rate,
			current: (MOCK_EVAL_RUNS[0] as EvalRunItem).summary.intent_hit_rate,
			delta:
				(MOCK_EVAL_RUNS[0] as EvalRunItem).summary.intent_hit_rate -
				(MOCK_EVAL_RUNS[1] as EvalRunItem).summary.intent_hit_rate,
		},
		pb_hit_rate: {
			baseline: (MOCK_EVAL_RUNS[1] as EvalRunItem).summary.pb_hit_rate,
			current: (MOCK_EVAL_RUNS[0] as EvalRunItem).summary.pb_hit_rate,
			delta:
				(MOCK_EVAL_RUNS[0] as EvalRunItem).summary.pb_hit_rate -
				(MOCK_EVAL_RUNS[1] as EvalRunItem).summary.pb_hit_rate,
		},
		badcase_candidate_count: {
			baseline: (MOCK_EVAL_RUNS[1] as EvalRunItem).summary.badcase_candidate_count,
			current: (MOCK_EVAL_RUNS[0] as EvalRunItem).summary.badcase_candidate_count,
			delta:
				(MOCK_EVAL_RUNS[0] as EvalRunItem).summary.badcase_candidate_count -
				(MOCK_EVAL_RUNS[1] as EvalRunItem).summary.badcase_candidate_count,
		},
	},
	sample_deltas: [
		{
			sample_id: "S001",
			message: "我想查一下订单状态",
			change_type: "unchanged_pass",
			baseline_sample_result_id: "esr_base_s001",
			current_sample_result_id: "esr_current_s001",
			baseline_intent_match: true,
			current_intent_match: true,
			baseline_pb_match: true,
			current_pb_match: true,
			baseline_badcase_candidate: false,
			current_badcase_candidate: false,
			baseline_actual_intent: "order_status_inquiry",
			current_actual_intent: "order_status_inquiry",
			baseline_actual_pb: "PB01_order_query",
			current_actual_pb: "PB01_order_query",
			root_cause_category: null,
		},
		{
			sample_id: "S002",
			message: "What is my order status",
			change_type: "fixed",
			baseline_sample_result_id: "esr_base_s002",
			current_sample_result_id: "esr_current_s002",
			baseline_intent_match: false,
			current_intent_match: true,
			baseline_pb_match: false,
			current_pb_match: true,
			baseline_badcase_candidate: true,
			current_badcase_candidate: false,
			baseline_actual_intent: "unknown",
			current_actual_intent: "order_status_inquiry",
			baseline_actual_pb: null,
			current_actual_pb: "PB01_order_query",
			root_cause_category: "intent_error",
		},
		{
			sample_id: "S003",
			message: "订单到哪了",
			change_type: "regressed",
			baseline_sample_result_id: "esr_base_s003",
			current_sample_result_id: "esr_current_s003",
			baseline_intent_match: true,
			current_intent_match: true,
			baseline_pb_match: true,
			current_pb_match: false,
			baseline_badcase_candidate: false,
			current_badcase_candidate: true,
			baseline_actual_intent: "order_status_inquiry",
			current_actual_intent: "order_status_inquiry",
			baseline_actual_pb: "PB01_order_query",
			current_actual_pb: "PB02_refund",
			root_cause_category: "playbook_error",
		},
	],
	fixed_count: 1,
	regressed_count: 1,
	unchanged_fail_count: 0,
	current_badcase_candidate_count: badcaseCandidateCount,
};

const MOCK_COMPARE_SNAPSHOTS: EvalRunCompareSnapshotItem[] = [];
const MOCK_REGRESSION_BATCHES: EvalRegressionBatchItem[] = [];

function buildCompareSnapshot(
	evalRunId: string,
	body: EvalRunComparePersistRequest,
): EvalRunCompareSnapshotItem {
	return {
		compare_id: `cmp_m07_${String(MOCK_COMPARE_SNAPSHOTS.length + 1).padStart(3, "0")}`,
		merchant_id: MOCK_MERCHANT_ID,
		baseline_run_id: body.baseline_run_id ?? MOCK_COMPARE_RESPONSE.baseline.eval_run_id,
		current_run_id: evalRunId,
		dataset_id: MOCK_COMPARE_RESPONSE.current.dataset_id,
		dataset_version: MOCK_COMPARE_RESPONSE.current.dataset_version,
		baseline_dataset_id: MOCK_COMPARE_RESPONSE.baseline.dataset_id,
		baseline_dataset_version: MOCK_COMPARE_RESPONSE.baseline.dataset_version,
		dataset_match: MOCK_COMPARE_RESPONSE.dataset_match,
		run_type_match:
			MOCK_COMPARE_RESPONSE.current.run_type === MOCK_COMPARE_RESPONSE.baseline.run_type,
		data_mode_match:
			MOCK_COMPARE_RESPONSE.current.data_mode === MOCK_COMPARE_RESPONSE.baseline.data_mode,
		fixed_count: MOCK_COMPARE_RESPONSE.fixed_count,
		regressed_count: MOCK_COMPARE_RESPONSE.regressed_count,
		unchanged_fail_count: MOCK_COMPARE_RESPONSE.unchanged_fail_count,
		current_badcase_candidate_count: MOCK_COMPARE_RESPONSE.current_badcase_candidate_count,
		metric_deltas: MOCK_COMPARE_RESPONSE.metric_deltas,
		sample_deltas: MOCK_COMPARE_RESPONSE.sample_deltas,
		created_by: body.created_by ?? "admin-console",
		compare_note: body.compare_note ?? null,
		created_at: "2026-05-08T12:00:00Z",
	};
}

const MOCK_BADCASES: BadcaseItem[] = MOCK_SAMPLE_RESULTS.filter((row) => row.badcase_candidate).map(
	(row, index) => ({
		badcase_id: row.badcase_id ?? `bc_p7e3b_${row.sample_id.toLowerCase()}`,
		merchant_id: MOCK_MERCHANT_ID,
		source_type: "eval_sample",
		status: "candidate",
		severity: index === 0 ? "P1" : "P2",
		impact_goal: "G1",
		eval_run_id: row.eval_run_id,
		sample_result_id: row.sample_result_id,
		sample_id: row.sample_id,
		trace_id: row.trace_id,
		admin_run_id: row.admin_run_id,
		rerun_status: row.rerun_status,
		message: row.message,
		root_cause_category: row.root_cause_category ?? "unknown",
		issue_code: row.root_cause_category ?? "unknown",
		root_cause_note: row.badcase_reason,
		responsible_asset_type: row.responsible_asset_type,
		responsible_asset_id: row.responsible_asset_id,
		responsible_asset_version: "2026-05-07",
		fix_suggestion: "回到 M02 意图规则补充表达覆盖后复跑本样本。",
		owner: "运营待分派",
		created_by: "codex-p7e3b",
		confirmed_by: null,
		fixed_at: null,
		graduated_at: null,
		graduated_by: null,
		graduation_dataset_id: null,
		graduation_dataset_version: null,
		graduation_note: null,
		created_at: row.created_at,
		updated_at: row.created_at,
	}),
);

function buildBadcaseEvents(badcase: BadcaseItem): BadcaseEventListResponse {
	const sampleSuffix = badcase.sample_id?.toLowerCase() ?? "unknown";
	const isPrimaryFlow = badcase.sample_id === "S003";
	const items: BadcaseEventListResponse["items"] = [
		{
			event_id: `bce_${sampleSuffix}_created`,
			badcase_id: badcase.badcase_id,
			merchant_id: badcase.merchant_id,
			event_type: "candidate_created",
			from_status: null,
			to_status: "candidate",
			rerun_status: null,
			actor: badcase.created_by,
			event_note: "Badcase 候选已创建",
			trace_id: null,
			admin_run_id: null,
			created_at: badcase.created_at,
		},
	];
	if (isPrimaryFlow) {
		items.push(
			{
				event_id: `bce_${sampleSuffix}_confirmed`,
				badcase_id: badcase.badcase_id,
				merchant_id: badcase.merchant_id,
				event_type: "status_changed",
				from_status: "candidate",
				to_status: "confirmed",
				rerun_status: null,
				actor: "admin-console",
				event_note: "Badcase 状态从 待确认 变为 已确认",
				trace_id: null,
				admin_run_id: null,
				created_at: "2026-05-07T13:00:00Z",
			},
			{
				event_id: `bce_${sampleSuffix}_fixed`,
				badcase_id: badcase.badcase_id,
				merchant_id: badcase.merchant_id,
				event_type: "status_changed",
				from_status: "confirmed",
				to_status: "fixed",
				rerun_status: null,
				actor: "admin-console",
				event_note: "Badcase 状态从 已确认 变为 已修复",
				trace_id: null,
				admin_run_id: null,
				created_at: "2026-05-07T13:05:00Z",
			},
			{
				event_id: `bce_${sampleSuffix}_rerun_passed`,
				badcase_id: badcase.badcase_id,
				merchant_id: badcase.merchant_id,
				event_type: "rerun_recorded",
				from_status: null,
				to_status: null,
				rerun_status: "rerun_passed",
				actor: "admin-console",
				event_note: "登记复跑结果：复跑通过",
				trace_id: `trace_m07_rerun_${sampleSuffix}`,
				admin_run_id: `run_m07_rerun_${sampleSuffix}`,
				created_at: "2026-05-07T13:12:00Z",
			},
			{
				event_id: `bce_${sampleSuffix}_writeback`,
				badcase_id: badcase.badcase_id,
				merchant_id: badcase.merchant_id,
				event_type: "regression_writeback",
				from_status: "fixed",
				to_status: "graduated",
				rerun_status: null,
				actor: "admin-console",
				event_note: "Badcase 已写回回归集：regression_badcase",
				trace_id: `trace_m07_rerun_${sampleSuffix}`,
				admin_run_id: `run_m07_rerun_${sampleSuffix}`,
				created_at: "2026-05-07T13:20:00Z",
			},
		);
	}
	if (badcase.status === "closed") {
		items.push({
			event_id: `bce_${sampleSuffix}_batch_closed`,
			badcase_id: badcase.badcase_id,
			merchant_id: badcase.merchant_id,
			event_type: "status_changed",
			from_status: "graduated",
			to_status: "closed",
			rerun_status: null,
			actor: "admin-console",
			event_note: "本轮批量回归通过，批量关闭符合条件的 Badcase。",
			trace_id: badcase.trace_id,
			admin_run_id: badcase.admin_run_id,
			created_at: "2026-05-08T13:05:00Z",
		});
	}
	return {
		badcase_id: badcase.badcase_id,
		items,
		total: items.length,
	};
}

function buildRegressionDatasetSample(
	existing: BadcaseItem,
	body: BadcaseRegressionWritebackRequest,
): RegressionDatasetSampleItem {
	const sample = MOCK_PER_SAMPLE.find((row) => row.sample_id === existing.sample_id);
	const sampleSuffix = existing.sample_id?.toLowerCase() ?? "unknown";
	return {
		regression_sample_id: `rgs_${sampleSuffix}`,
		merchant_id: existing.merchant_id,
		target_dataset_id: body.target_dataset_id,
		target_dataset_version: body.target_dataset_version ?? null,
		sample_id: existing.sample_id ?? `badcase_${sampleSuffix}`,
		message: sample?.message ?? existing.message ?? "",
		expected_intent: sample?.expected_intent ?? "order_status_inquiry",
		expected_pb: sample?.expected_pb ?? null,
		source_badcase_id: existing.badcase_id,
		source_eval_run_id: existing.eval_run_id,
		source_sample_result_id: existing.sample_result_id,
		source_sample_id: existing.sample_id,
		source_trace_id: existing.trace_id ?? `trace_m07_rerun_${sampleSuffix}`,
		source_admin_run_id: existing.admin_run_id ?? `run_m07_rerun_${sampleSuffix}`,
		root_cause_category: existing.root_cause_category,
		root_cause_hint: existing.root_cause_note,
		responsible_asset_type: existing.responsible_asset_type,
		responsible_asset_id: existing.responsible_asset_id,
		writeback_note: body.writeback_note ?? null,
		created_by: body.updated_by ?? "admin-console",
		created_at: "2026-05-07T13:20:00Z",
	};
}

function buildRegressionBatch(body: EvalRegressionBatchCreateRequest): EvalRegressionBatchItem {
	const datasetId = body.target_dataset_id ?? "regression_badcase";
	const sampleIds = ["rgs_s003"];
	return {
		batch_id: `erb_m07_${String(MOCK_REGRESSION_BATCHES.length + 1).padStart(3, "0")}`,
		merchant_id: body.merchant_id,
		target_dataset_id: datasetId,
		target_dataset_version: body.target_dataset_version ?? "2026-05-08",
		status: "scheduled",
		mode: body.mode ?? "both",
		data_mode: body.data_mode ?? "mock",
		sample_count: sampleIds.length,
		sample_ids: sampleIds,
		eval_run_id: null,
		created_by: body.created_by ?? "admin-console",
		schedule_note: body.schedule_note ?? null,
		created_at: "2026-05-08T13:00:00Z",
		updated_at: "2026-05-08T13:00:00Z",
	};
}

function buildRegressionBatchExecuteResponse(
	batch: EvalRegressionBatchItem,
	body: EvalRegressionBatchExecuteRequest,
): EvalRegressionBatchExecuteResponse {
	const executedAt = "2026-05-08T13:02:00Z";
	const evalRunId = `er_${batch.batch_id.replace(/^erb_/, "batch_")}`;
	const regressionSamples = batch.sample_ids
		.map(() =>
			buildRegressionDatasetSample(MOCK_BADCASES[0] as BadcaseItem, {
				target_dataset_id: batch.target_dataset_id,
				target_dataset_version: batch.target_dataset_version,
				writeback_note: "复跑通过后写回回归集，后续批量回归持续覆盖。",
				updated_by: "admin-console",
			}),
		)
		.slice(0, batch.sample_count);
	const perSample = regressionSamples.map((row, index) =>
		buildPassingBatchSampleResult(row, evalRunId, index),
	);
	const evalRun: EvalRunResponse = {
		...(MOCK_EVAL_RUNS[0] as EvalRunItem),
		eval_run_id: evalRunId,
		run_name: "批量回归任务已执行",
		dataset_id: batch.target_dataset_id,
		dataset_version: batch.target_dataset_version ?? "inline",
		mode: batch.mode,
		data_mode: batch.data_mode,
		comparison_role: "current",
		created_by: body.executed_by ?? "admin-console",
		notes: body.execution_note ?? "执行已写回样本的批量回归。",
		summary: {
			total: perSample.length,
			intent_passed: perSample.filter((row) => row.intent_match).length,
			intent_hit_rate:
				perSample.length === 0
					? 0
					: perSample.filter((row) => row.intent_match).length / perSample.length,
			pb_total_evaluable: perSample.filter((row) => row.pb_match !== null).length,
			pb_passed: perSample.filter((row) => row.pb_match === true).length,
			pb_hit_rate:
				perSample.filter((row) => row.pb_match !== null).length === 0
					? 0
					: perSample.filter((row) => row.pb_match === true).length /
						perSample.filter((row) => row.pb_match !== null).length,
			badcase_candidate_count: perSample.filter((row) => row.badcase_candidate).length,
		},
		started_at: "2026-05-08T13:01:00Z",
		completed_at: executedAt,
		created_at: "2026-05-08T13:01:00Z",
		updated_at: executedAt,
		per_sample: perSample,
	};
	const completedBatch: EvalRegressionBatchItem = {
		...batch,
		status: "completed",
		eval_run_id: evalRun.eval_run_id,
		updated_at: executedAt,
	};
	Object.assign(batch, completedBatch);
	MOCK_EVAL_RUNS.unshift(evalRun);
	return { batch: completedBatch, eval_run: evalRun };
}

function buildRegressionBatchCloseBadcasesResponse(
	batch: EvalRegressionBatchItem,
	body: EvalRegressionBatchCloseBadcasesRequest,
): EvalRegressionBatchCloseBadcasesResponse {
	const dryRun = body.dry_run ?? false;
	const badcase = MOCK_BADCASES.find((item) => item.badcase_id === "bc_p7e3b_s003");
	const sample = buildRegressionDatasetSample((badcase ?? MOCK_BADCASES[0]) as BadcaseItem, {
		target_dataset_id: batch.target_dataset_id,
		target_dataset_version: batch.target_dataset_version,
		writeback_note: "复跑通过后写回回归集，后续批量回归持续覆盖。",
		updated_by: body.updated_by ?? "admin-console",
	});
	const statusBefore: BadcaseItem["status"] = "graduated";
	if (badcase && !dryRun) {
		Object.assign(badcase, {
			status: "closed",
			trace_id:
				badcase.trace_id ?? `trace_m07_rerun_${badcase.sample_id?.toLowerCase() ?? "unknown"}`,
			admin_run_id:
				badcase.admin_run_id ?? `run_m07_rerun_${badcase.sample_id?.toLowerCase() ?? "unknown"}`,
			rerun_status: "rerun_passed",
			graduated_at: badcase.graduated_at ?? "2026-05-07T13:20:00Z",
			graduated_by: badcase.graduated_by ?? body.updated_by ?? "admin-console",
			graduation_dataset_id: badcase.graduation_dataset_id ?? batch.target_dataset_id,
			graduation_dataset_version:
				badcase.graduation_dataset_version ?? batch.target_dataset_version ?? "2026-05-08",
			graduation_note: badcase.graduation_note ?? "复跑通过后写回回归集，后续批量回归持续覆盖。",
			updated_at: "2026-05-08T13:05:00Z",
		});
	}
	return {
		batch,
		dry_run: dryRun,
		total: 1,
		closed_count: dryRun ? 0 : 1,
		skipped_count: 0,
		items: [
			{
				regression_sample_id: sample.regression_sample_id,
				sample_id: sample.sample_id,
				badcase_id: sample.source_badcase_id,
				eval_sample_result_id: `esr_${batch.eval_run_id?.replace(/^er_/, "")}_${sample.sample_id.toLowerCase()}`,
				decision: dryRun ? "eligible" : "closed",
				reason: "本轮批量回归通过，满足批量关闭条件",
				status_before: statusBefore,
				status_after: dryRun ? statusBefore : "closed",
				intent_match: true,
				pb_match: true,
			},
		],
	};
}

function buildRegressionWritebackResponse(
	existing: BadcaseItem,
	body: BadcaseRegressionWritebackRequest,
): BadcaseRegressionWritebackResponse {
	const sampleSuffix = existing.sample_id?.toLowerCase() ?? "unknown";
	const sample = buildRegressionDatasetSample(existing, body);
	const updated: BadcaseItem = {
		...existing,
		status: "graduated",
		trace_id: existing.trace_id ?? `trace_m07_rerun_${sampleSuffix}`,
		admin_run_id: existing.admin_run_id ?? `run_m07_rerun_${sampleSuffix}`,
		rerun_status: "rerun_passed",
		graduated_at: "2026-05-07T13:20:00Z",
		graduated_by: body.updated_by ?? "admin-console",
		graduation_dataset_id: body.target_dataset_id,
		graduation_dataset_version: body.target_dataset_version ?? null,
		graduation_note: body.writeback_note ?? null,
		updated_at: "2026-05-07T13:20:00Z",
	};
	return {
		badcase: updated,
		sample,
		already_exists: false,
	};
}

function buildAutoCloseBadcase(existing: BadcaseItem, body: BadcaseAutoCloseRequest): BadcaseItem {
	const sampleSuffix = existing.sample_id?.toLowerCase() ?? "unknown";
	return {
		...existing,
		status: "closed",
		trace_id: existing.trace_id ?? `trace_m07_rerun_${sampleSuffix}`,
		admin_run_id: existing.admin_run_id ?? `run_m07_rerun_${sampleSuffix}`,
		rerun_status: "rerun_passed",
		graduated_at: existing.graduated_at ?? "2026-05-07T13:20:00Z",
		graduated_by: existing.graduated_by ?? body.updated_by ?? "admin-console",
		graduation_dataset_id: existing.graduation_dataset_id ?? "regression_badcase",
		graduation_dataset_version: existing.graduation_dataset_version ?? "2026-05-08",
		graduation_note: existing.graduation_note ?? "复跑通过后写回回归集，后续批量回归持续覆盖。",
		updated_at: "2026-05-07T13:25:00Z",
	};
}

function buildExecuteRerunResponse(
	existing: BadcaseItem,
	body: BadcaseExecuteRerunRequest,
): BadcaseExecuteRerunResponse {
	const sampleSuffix = existing.sample_id?.toLowerCase() ?? "unknown";
	const traceId = `trace_m07_execute_${sampleSuffix}`;
	const runId = `run_${traceId}`;
	const sessionId = `session_m07_execute_${sampleSuffix}`;
	const updated: BadcaseItem = {
		...existing,
		status: "fixed",
		trace_id: traceId,
		admin_run_id: runId,
		rerun_status: "rerun_passed",
		updated_at: "2026-05-07T13:14:00Z",
	};
	return {
		badcase: updated,
		rerun_status: "rerun_passed",
		trace_id: traceId,
		admin_run_id: runId,
		expected_intent: "order_status_inquiry",
		actual_intent: "order_status_inquiry",
		intent_match: true,
		expected_pb: "PB01_order_query",
		actual_pb: "PB01_order_query",
		pb_match: true,
		final_reply: "已查询到订单状态，当前订单正在运输中。",
		run: {
			run_id: runId,
			trace_id: traceId,
			session_id: body.session_id ?? sessionId,
			status: "completed",
			final_action_type: "inform",
			final_outcome: "success",
			final_reply: "已查询到订单状态，当前订单正在运输中。",
			total_latency_ms: 128,
			events: [],
			messages_response: {
				trace_id: traceId,
				session_id: body.session_id ?? sessionId,
				intent: {
					intent: "order_status_inquiry",
					service_stage: "after_sale",
					dialogue_signal: { sentiment: "neutral", intent_shift: "none" },
					intent_continuity: "switch",
					confidence: 0.96,
					topic: "订单状态",
					secondary_intent: null,
					implementation_level: "L2",
					l2_matched_rule_id: "T002",
				},
				recommended_playbook: {
					id: "PB01_order_query",
					name: "订单查询方案 mock",
					guidance: "查询订单状态并给出下一步说明。",
					fallback_hint: "信息不足时追问订单号。",
					version: "V0.1",
				},
				recommended_skills: ["SK01", "SK02"],
				universal_skills: ["SK05", "SK11"],
				match_mode: "config_mapping",
				reply_text: "已查询到订单状态，当前订单正在运输中。",
				phase_trace: [],
				history_turns_included: 0,
				runtime_capabilities: {
					phase4_runner_mode: "static",
					prompt_effect_mode: "rendered_not_consumed",
					rag_mode: "null",
					session_mode: "pg_session",
					quality_mode: "q_fast_grounding_starter",
					handoff_mode: "contract_only",
					shopify_read_mode: "not_requested",
					shopify_write_mode: "disabled",
				},
				reply_placeholder: "已查询到订单状态，当前订单正在运输中。",
				final_action_type: "inform",
				final_outcome: "success",
			},
		},
	};
}

export function registerM07Fixtures(): void {
	registerFixture<CxScoreRunResponse>({
		pathname: "/admin/v1/eval/cx-score/run",
		methods: ["POST"],
		resolve: () =>
			({
				status: 200,
				data: {
					scored: true,
					eligible: true,
					unscored_reason: null,
					cx_score: 4,
					reasons: {
						resolution_outcome: "High",
						answer_clarity_completeness: "High",
						customer_effort: "Low",
						strong_emotion: "Neutral",
						product_service_feedback: "Neutral",
						policy_feedback: "Neutral",
						compliance_risk: "Neutral",
					},
					explanation: "（mock）问题已解决、表达清晰、用户费力度低。",
					compliance_risk: "Neutral",
					compliance_capped: false,
					model: "openai/gpt-4o",
					source: "admin-panel",
				},
			}) satisfies MockResponse<CxScoreRunResponse>,
	});

	registerFixture({
		pathname: "/admin/v1/eval/golden-15/run",
		methods: ["POST"],
		resolve: () => ({
			status: 200,
			data: {
				version: "2026-04-28T00:00:00Z",
				author: "mock@m07",
				scope: "golden15",
				pass_threshold: 0.8,
				total: 15,
				passed: 15,
				overall_pass_rate: 1,
				meets_threshold: true,
				intent_passed: 15,
				pb_passed: 14,
				match_mode_passed: 15,
				phase_passed: 15,
				phase5_passed: 13,
				per_sample: [],
			},
		}),
	});

	registerFixture<SamplesListResponse>({
		pathname: "/admin/v1/eval/samples",
		methods: ["GET"],
		resolve: () =>
			({
				status: 200,
				data: {
					version: "2026-04-28T00:00:00Z",
					author: "mock@m07",
					total: MOCK_SAMPLES.length,
					samples: MOCK_SAMPLES,
				},
			}) satisfies MockResponse<SamplesListResponse>,
	});

	registerFixture<RegressionResponse>({
		pathname: "/admin/v1/eval/regress",
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as { mode?: RegressionMode };
			const mode: RegressionMode = body.mode ?? "both";
			return {
				status: 200,
				data: buildResponse(mode),
				delay_ms: 250,
			} satisfies MockResponse<RegressionResponse>;
		},
	});

	registerFixture<EvalRunListResponse>({
		pathname: "/admin/v1/eval/runs",
		methods: ["GET"],
		resolve: () =>
			({
				status: 200,
				data: {
					items: MOCK_EVAL_RUNS,
					total: MOCK_EVAL_RUNS.length,
				},
			}) satisfies MockResponse<EvalRunListResponse>,
	});

	registerFixture<EvalRunResponse>({
		pathname: "/admin/v1/eval/runs",
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as Partial<EvalRunCreateRequest>;
			const base = MOCK_EVAL_RUNS[0] as EvalRunItem;
			const sampleCount = body.samples?.length ?? MOCK_SAMPLE_RESULTS.length;
			const mode = body.mode ?? "both";
			const createdRun: EvalRunResponse = {
				...base,
				eval_run_id: "er_p7e3c_inline_mock",
				run_name: body.run_name ?? "P7E-3C 留痕运行",
				mode,
				dataset_id: body.dataset_id ?? base.dataset_id,
				dataset_version: body.dataset_version ?? "inline",
				data_mode: body.data_mode ?? base.data_mode,
				comparison_role: body.comparison_role ?? "current",
				created_by: body.created_by ?? base.created_by,
				notes: body.notes ?? base.notes,
				summary: {
					total: sampleCount,
					intent_passed: mode === "playbook" ? 0 : sampleCount,
					intent_hit_rate: mode === "playbook" ? 0 : 1,
					pb_total_evaluable: mode === "intent" ? 0 : sampleCount,
					pb_passed: mode === "intent" ? 0 : sampleCount,
					pb_hit_rate: mode === "intent" ? 0 : 1,
					badcase_candidate_count: 0,
				},
				per_sample: MOCK_SAMPLE_RESULTS.slice(0, sampleCount),
			};
			const existingIndex = MOCK_EVAL_RUNS.findIndex(
				(run) => run.eval_run_id === createdRun.eval_run_id,
			);
			if (existingIndex >= 0) {
				MOCK_EVAL_RUNS.splice(existingIndex, 1);
			}
			MOCK_EVAL_RUNS.unshift(createdRun);
			return {
				status: 200,
				data: createdRun,
				delay_ms: 250,
			} satisfies MockResponse<EvalRunResponse>;
		},
	});

	registerFixture<EvalRunResponse>({
		pathname: "/admin/v1/eval/runs/:eval_run_id",
		methods: ["GET"],
		resolve: () =>
			({
				status: 200,
				data: {
					...(MOCK_EVAL_RUNS[0] as EvalRunItem),
					per_sample: MOCK_SAMPLE_RESULTS,
				},
			}) satisfies MockResponse<EvalRunResponse>,
	});

	registerFixture<EvalRunCompareResponse>({
		pathname: "/admin/v1/eval/runs/:eval_run_id/compare",
		methods: ["POST"],
		resolve: () =>
			({
				status: 200,
				data: MOCK_COMPARE_RESPONSE,
			}) satisfies MockResponse<EvalRunCompareResponse>,
	});

	registerFixture<EvalRunCompareSnapshotItem>({
		pathname: "/admin/v1/eval/runs/:eval_run_id/compare/snapshots",
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as EvalRunComparePersistRequest;
			const snapshot = buildCompareSnapshot(
				ctx.params.eval_run_id ?? MOCK_CURRENT_EVAL_RUN_ID,
				body,
			);
			MOCK_COMPARE_SNAPSHOTS.unshift(snapshot);
			return {
				status: 200,
				data: snapshot,
				delay_ms: 180,
			} satisfies MockResponse<EvalRunCompareSnapshotItem>;
		},
	});

	registerFixture<EvalRunCompareSnapshotListResponse>({
		pathname: "/admin/v1/eval/compare",
		methods: ["GET"],
		resolve: (ctx) => {
			const currentRunId = ctx.query.current_run_id;
			const items = currentRunId
				? MOCK_COMPARE_SNAPSHOTS.filter((item) => item.current_run_id === currentRunId)
				: MOCK_COMPARE_SNAPSHOTS;
			return {
				status: 200,
				data: { items, total: items.length },
			} satisfies MockResponse<EvalRunCompareSnapshotListResponse>;
		},
	});

	registerFixture<AssetChangeAuditListResponse>({
		pathname: "/admin/v1/eval/asset-change-audits",
		methods: ["GET"],
		resolve: () =>
			({
				status: 200,
				data: {
					items: MOCK_ASSET_CHANGE_AUDITS,
					total: MOCK_ASSET_CHANGE_AUDITS.length,
				},
			}) satisfies MockResponse<AssetChangeAuditListResponse>,
	});

	registerFixture<EvalSampleResultListResponse>({
		pathname: "/admin/v1/eval/runs/:eval_run_id/samples",
		methods: ["GET"],
		resolve: (ctx) =>
			({
				status: 200,
				data: {
					eval_run_id: ctx.params.eval_run_id ?? MOCK_EVAL_RUN_ID,
					items: MOCK_SAMPLE_RESULTS,
					total: MOCK_SAMPLE_RESULTS.length,
				},
			}) satisfies MockResponse<EvalSampleResultListResponse>,
	});

	registerFixture<BadcaseListResponse>({
		pathname: "/admin/v1/eval/badcases",
		methods: ["GET"],
		resolve: () =>
			({
				status: 200,
				data: {
					items: MOCK_BADCASES,
					total: MOCK_BADCASES.length,
				},
			}) satisfies MockResponse<BadcaseListResponse>,
	});

	registerFixture<BadcaseEventListResponse | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id/events",
		methods: ["GET"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} events not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			return {
				status: 200,
				data: buildBadcaseEvents(existing),
			} satisfies MockResponse<BadcaseEventListResponse>;
		},
	});

	registerFixture<RegressionDatasetSampleListResponse>({
		pathname: "/admin/v1/eval/regression-samples",
		methods: ["GET"],
		resolve: () =>
			({
				status: 200,
				data: {
					items: [
						buildRegressionDatasetSample(MOCK_BADCASES[0] as BadcaseItem, {
							target_dataset_id: "regression_badcase",
							target_dataset_version: "2026-05-08",
							writeback_note: "复跑通过后写回回归集，后续批量回归持续覆盖。",
							updated_by: "admin-console",
						}),
					],
					total: 1,
				},
			}) satisfies MockResponse<RegressionDatasetSampleListResponse>,
	});

	registerFixture<EvalRegressionBatchListResponse>({
		pathname: "/admin/v1/eval/regression-batches",
		methods: ["GET"],
		resolve: (ctx) => {
			const targetDatasetId = ctx.query.target_dataset_id;
			const items = targetDatasetId
				? MOCK_REGRESSION_BATCHES.filter((item) => item.target_dataset_id === targetDatasetId)
				: MOCK_REGRESSION_BATCHES;
			return {
				status: 200,
				data: { items, total: items.length },
			} satisfies MockResponse<EvalRegressionBatchListResponse>;
		},
	});

	registerFixture<EvalRegressionBatchItem>({
		pathname: "/admin/v1/eval/regression-batches",
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as EvalRegressionBatchCreateRequest;
			const batch = buildRegressionBatch(body);
			MOCK_REGRESSION_BATCHES.unshift(batch);
			return {
				status: 200,
				data: batch,
				delay_ms: 180,
			} satisfies MockResponse<EvalRegressionBatchItem>;
		},
	});

	registerFixture<EvalRegressionBatchExecuteResponse | { detail: string }>({
		pathname: "/admin/v1/eval/regression-batches/:batch_id/execute",
		methods: ["POST"],
		resolve: (ctx) => {
			const batch = MOCK_REGRESSION_BATCHES.find((item) => item.batch_id === ctx.params.batch_id);
			if (!batch || batch.status !== "scheduled") {
				return {
					status: 400,
					data: { detail: "regression batch is not ready to execute" },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as EvalRegressionBatchExecuteRequest;
			return {
				status: 200,
				data: buildRegressionBatchExecuteResponse(batch, body),
				delay_ms: 220,
			} satisfies MockResponse<EvalRegressionBatchExecuteResponse>;
		},
	});

	registerFixture<EvalRegressionBatchCloseBadcasesResponse | { detail: string }>({
		pathname: "/admin/v1/eval/regression-batches/:batch_id/close-badcases",
		methods: ["POST"],
		resolve: (ctx) => {
			const batch = MOCK_REGRESSION_BATCHES.find((item) => item.batch_id === ctx.params.batch_id);
			if (!batch || batch.status !== "completed" || !batch.eval_run_id) {
				return {
					status: 400,
					data: { detail: "regression batch must be completed before batch close" },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as EvalRegressionBatchCloseBadcasesRequest;
			return {
				status: 200,
				data: buildRegressionBatchCloseBadcasesResponse(batch, body),
				delay_ms: 180,
			} satisfies MockResponse<EvalRegressionBatchCloseBadcasesResponse>;
		},
	});

	registerFixture<BadcaseItem | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id",
		methods: ["GET", "PATCH"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			if (ctx.method === "GET") {
				return { status: 200, data: existing } satisfies MockResponse<BadcaseItem>;
			}
			const body = (ctx.body ?? {}) as BadcaseUpdateRequest;
			let confirmedBy = existing.confirmed_by;
			if (body.status === "confirmed") {
				confirmedBy = body.updated_by ?? "admin-console";
			}
			const updated: BadcaseItem = {
				...existing,
				status: body.status ?? existing.status,
				severity: body.severity ?? existing.severity,
				impact_goal: body.impact_goal ?? existing.impact_goal,
				root_cause_category: body.root_cause_category ?? existing.root_cause_category,
				issue_code: body.issue_code ?? existing.issue_code,
				root_cause_note: body.root_cause_note ?? existing.root_cause_note,
				responsible_asset_type: body.responsible_asset_type ?? existing.responsible_asset_type,
				responsible_asset_id: body.responsible_asset_id ?? existing.responsible_asset_id,
				responsible_asset_version:
					body.responsible_asset_version ?? existing.responsible_asset_version,
				fix_suggestion: body.fix_suggestion ?? existing.fix_suggestion,
				owner: body.owner ?? existing.owner,
				confirmed_by: confirmedBy,
				fixed_at: body.status === "fixed" ? "2026-05-07T13:05:00Z" : existing.fixed_at,
				updated_at: "2026-05-07T13:05:00Z",
			};
			return { status: 200, data: updated } satisfies MockResponse<BadcaseItem>;
		},
	});

	registerFixture<BadcaseItem | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id/rerun",
		methods: ["POST"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as BadcaseRerunRequest;
			const updated: BadcaseItem = {
				...existing,
				status: "fixed",
				trace_id:
					body.trace_id ?? `trace_m07_rerun_${existing.sample_id?.toLowerCase() ?? "unknown"}`,
				admin_run_id:
					body.admin_run_id ?? `run_m07_rerun_${existing.sample_id?.toLowerCase() ?? "unknown"}`,
				rerun_status: body.rerun_status,
				updated_at: "2026-05-07T13:12:00Z",
			};
			return { status: 200, data: updated } satisfies MockResponse<BadcaseItem>;
		},
	});

	registerFixture<BadcaseExecuteRerunResponse | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id/rerun/execute",
		methods: ["POST"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as BadcaseExecuteRerunRequest;
			return {
				status: 200,
				data: buildExecuteRerunResponse(existing, body),
				delay_ms: 250,
			} satisfies MockResponse<BadcaseExecuteRerunResponse>;
		},
	});

	registerFixture<BadcaseItem | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id/graduate",
		methods: ["POST"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as BadcaseGraduationRequest;
			const updated: BadcaseItem = {
				...existing,
				status: "graduated",
				trace_id:
					existing.trace_id ?? `trace_m07_rerun_${existing.sample_id?.toLowerCase() ?? "unknown"}`,
				admin_run_id:
					existing.admin_run_id ??
					`run_m07_rerun_${existing.sample_id?.toLowerCase() ?? "unknown"}`,
				rerun_status: "rerun_passed",
				graduated_at: "2026-05-07T13:20:00Z",
				graduated_by: body.updated_by ?? "admin-console",
				graduation_dataset_id: body.target_dataset_id,
				graduation_dataset_version: body.target_dataset_version ?? null,
				graduation_note: body.graduation_note ?? null,
				updated_at: "2026-05-07T13:20:00Z",
			};
			return { status: 200, data: updated } satisfies MockResponse<BadcaseItem>;
		},
	});

	registerFixture<BadcaseRegressionWritebackResponse | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id/writeback/regression",
		methods: ["POST"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as BadcaseRegressionWritebackRequest;
			return {
				status: 200,
				data: buildRegressionWritebackResponse(existing, body),
				delay_ms: 200,
			} satisfies MockResponse<BadcaseRegressionWritebackResponse>;
		},
	});

	registerFixture<BadcaseItem | { detail: string }>({
		pathname: "/admin/v1/eval/badcases/:badcase_id/auto-close",
		methods: ["POST"],
		resolve: (ctx) => {
			const badcaseId = ctx.params.badcase_id;
			const existing = MOCK_BADCASES.find((item) => item.badcase_id === badcaseId);
			if (!existing) {
				return {
					status: 404,
					data: { detail: `mock badcase ${badcaseId} not found` },
				} satisfies MockResponse<{ detail: string }>;
			}
			const body = (ctx.body ?? {}) as BadcaseAutoCloseRequest;
			return {
				status: 200,
				data: buildAutoCloseBadcase(existing, body),
				delay_ms: 160,
			} satisfies MockResponse<BadcaseItem>;
		},
	});
}
